import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, Subscription, map } from 'rxjs';
import { Market, Region } from '../../../../model/shared/platform-region.model';
import { PlatformRegionService } from '../../../../service/shared/platform-region.service';
import { ADMIN_SERVICE } from '../../../../service/service-url';

@Injectable({
    providedIn: 'root'
})
export class ChatRegionMarketService implements OnDestroy {

    private _regionsList: Region[] = [];
    private _marketList: Market[] = [];
    private _selected_region_code: string | null = null;
    private _selected_region_label: string | null = null;
    private _selected_market_code: string | null = null;

    private marketSubject = new Subject<void>();
    marketSubject$: Observable<void> = this.marketSubject.asObservable();

    private platformMarketSub: Subscription;

    constructor(
        private httpClient: HttpClient,
        private platformRegionService: PlatformRegionService
    ) { }

    ngOnDestroy(): void {
        this.platformMarketSub?.unsubscribe();
    }
    initialize(): void {
        this._regionsList = [...this.platformRegionService.regionsList];
        this._selected_region_code = this.platformRegionService.selected_region_code;
        this._selected_region_label = this.platformRegionService.selected_region_label;
        this._selected_market_code = this.platformRegionService.selected_market_code;

        const platformMarkets = this.platformRegionService.marketList;
        if (platformMarkets && platformMarkets.length > 0) {
            this._marketList = [...platformMarkets];
            this.marketSubject.next();
        } else {
            this.platformMarketSub?.unsubscribe();
            this.platformMarketSub = this.platformRegionService.marketSubject$.subscribe(() => {
                this._marketList = this.platformRegionService.marketList
                    ? [...this.platformRegionService.marketList]
                    : [];
                this._selected_market_code = this.platformRegionService.selected_market_code;
                this.marketSubject.next();
                this.platformMarketSub?.unsubscribe();
            });
        }
    }

    get regionsList(): Region[] {
        return this._regionsList;
    }

    get marketList(): Market[] {
        return this._marketList;
    }

    get selected_region_code(): string | null {
        return this._selected_region_code;
    }

    get selected_region_label(): string | null {
        return this._selected_region_label;
    }

    get selected_market_code(): string | null {
        return this._selected_market_code;
    }

    setSelectedRegionCode(regionCode: string): void {
        this._selected_region_code = regionCode || null;
    }

    setSelectedRegionLabel(label: string): void {
        this._selected_region_label = label || null;
    }

    setSelectedMarketCode(marketCode: string | null): void {
        this._selected_market_code = marketCode ?? null;
    }

    async setDataForSelectedRegion(regionName: string): Promise<void> {
        const region = this._regionsList.find(r => r.name === regionName || r.label === regionName) || this._regionsList[0];
        this._selected_region_code = region?.name || null;
        this._selected_region_label = region?.label || region?.name || null;

        this._marketList = await this.fetchMarkets(region.name);

        if (this._marketList && this._marketList.length > 0) {
            this._selected_market_code = this._marketList[0].primary_market.code;
        } else {
            this._marketList = [];
            this._selected_market_code = null;
        }

        this.marketSubject.next();
    }

    private fetchMarkets(regionName: string): Promise<Market[]> {
        return new Promise((resolve) => {
            this.httpClient
                .get<Market[]>(`${ADMIN_SERVICE.getDetailMarketList}?region_name=${regionName}`)
                .pipe(map(list => list.sort((a, b) => a.primary_market.name.localeCompare(b.primary_market.name))))
                .subscribe({
                    next: (markets) => resolve(markets),
                    error: () => resolve([])
                });
        });
    }
}
